import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ArrowLeft, Plus } from 'lucide-react';
import { toast } from 'sonner';
import InstitutionImportPreviewRow from './InstitutionImportPreviewRow';

// Investment types the MyInvestFolio backend understands. Kept in sync with
// routes/csv_upload.py::TYPE_LABELS keys.
const INVESTMENT_TYPES = [
  { value: 'quoted_stocks', label: 'Quoted Stocks' },
  { value: 'government_bond', label: 'Government Bond' },
  { value: 'corporate_bond', label: 'Corporate Bond' },
  { value: 'mutual_fund', label: 'Mutual Fund' },
  { value: 'treasury_bill', label: 'Treasury Bill' },
  { value: 'fixed_deposit', label: 'Fixed Deposit' },
  { value: 'bank_balance', label: 'Bank Balance' },
  { value: 'etf', label: 'ETF' },
  { value: 'private_equity', label: 'Private Equity' },
  { value: 'convertible', label: 'Convertible' },
  { value: 'safe', label: 'SAFE Investment' },
  { value: 'crypto', label: 'Cryptocurrency' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'gold', label: 'Gold' },
];

// Pre-confirm the confirm-and-save screen. Every row is editable and
// deletable so the user is the final safety net for anything the generic
// extractor got slightly wrong.
const InstitutionImportPreview = ({
  preview, targetLabel = 'Portfolio Investments',
  saving, onConfirm, onBack,
}) => {
  const [investmentType, setInvestmentType] = useState('quoted_stocks');
  const [editedRows, setEditedRows] = useState([]);
  const [autoEditIdx, setAutoEditIdx] = useState(null);

  // Reset the editable copy whenever a new preview arrives.
  useEffect(() => {
    setEditedRows(preview?.rows ? [...preview.rows] : []);
    setAutoEditIdx(null);
  }, [preview]);

  const validCount = useMemo(
    () => editedRows.filter((r) => (r.asset_name || '').trim() && r.market_value != null).length,
    [editedRows],
  );

  if (!preview) return null;
  const { institution_display_name, institution_id } = preview;

  const updateRow = (idx) => (next) => {
    setEditedRows((prev) => prev.map((r, i) => (i === idx ? { ...next, edited: true } : r)));
    // Row committed → forget its auto-edit sticky flag so the next "Add"
    // reliably re-triggers auto-edit even if it lands on the same index.
    if (idx === autoEditIdx) setAutoEditIdx(null);
  };
  const deleteRow = (idx) => () => {
    // Capture the row BEFORE the state update — reading it inside the
    // setEditedRows updater is unsafe because React 18 may defer the
    // updater to the next render, at which point the toast() call below
    // would see `removed === undefined` and silently no-op.
    const removed = editedRows[idx];
    setEditedRows((prev) => prev.filter((_, i) => i !== idx));
    if (idx === autoEditIdx) setAutoEditIdx(null);
    // 5-second undo — a single mis-click on the trash icon shouldn't lose
    // data. Sonner auto-dismisses after 5s, at which point the deletion is
    // final. Clicking "Undo" restores the row at the same index.
    if (removed) {
      const label = (removed.asset_name || '').trim() || 'Row';
      toast(`${label} removed`, {
        description: 'Nothing is saved until you click confirm.',
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: () => {
            setEditedRows((prev) => {
              const next = [...prev];
              next.splice(Math.min(idx, next.length), 0, removed);
              return next;
            });
          },
        },
      });
    }
  };
  const addRow = () => {
    setEditedRows((prev) => {
      const next = [
        ...prev,
        { asset_name: '', currency: null, market_value: null, confidence: 1, edited: true },
      ];
      setAutoEditIdx(next.length - 1);
      return next;
    });
  };

  const handleConfirm = () => {
    // Rebuild raw_source_row from the edited fields so the commit endpoint
    // sees the User's corrections, not the extractor's originals.
    const rows = editedRows
      .filter((r) => (r.asset_name || '').trim() && r.market_value != null)
      .map((r) => ({
        ...r,
        raw_source_row: {
          Name: r.asset_name,
          "Current Value": String(r.market_value),
          Currency: r.currency || '',
          "Owner Type": 'direct',
        },
      }));
    onConfirm({ institution_id, investment_type: investmentType, rows });
  };

  return (
    <div className="space-y-4" data-testid="institution-import-preview">
      <div>
        <h3 className="text-lg font-serif text-slate-900">
          We extracted {editedRows.length} {editedRows.length === 1 ? 'holding' : 'holdings'} from your {institution_display_name} file
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          Please review each row — edit anything that&rsquo;s off, or remove rows that aren&rsquo;t holdings.
          Nothing is saved until you click confirm.
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 p-3 bg-slate-50 flex items-center gap-3">
        <label htmlFor="ii-type" className="text-sm text-slate-700 whitespace-nowrap">
          These holdings are:
        </label>
        <select
          id="ii-type" value={investmentType}
          onChange={(e) => setInvestmentType(e.target.value)}
          disabled={saving}
          className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="institution-import-preview-type"
        >
          {INVESTMENT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Asset name</th>
              <th className="text-left px-3 py-2 font-medium">Currency</th>
              <th className="text-right px-3 py-2 font-medium">Value</th>
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {editedRows.map((r, idx) => (
              <InstitutionImportPreviewRow
                key={idx} row={r} index={idx} disabled={saving}
                autoEdit={idx === autoEditIdx}
                onChange={updateRow(idx)} onDelete={deleteRow(idx)}
              />
            ))}
            {editedRows.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-sm text-slate-500">
                No rows left. Add one manually below, or choose a different file.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-start">
        <Button
          type="button" variant="outline" size="sm" onClick={addRow} disabled={saving}
          className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
          data-testid="institution-import-preview-add-row"
        >
          <Plus className="w-4 h-4 mr-1" /> Add a holding manually
        </Button>
      </div>

      <div className="flex items-center justify-between gap-2 pt-2">
        <Button variant="outline" onClick={onBack} disabled={saving}
          data-testid="institution-import-preview-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Choose a different file
        </Button>
        <Button
          onClick={handleConfirm} disabled={saving || validCount === 0}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="institution-import-preview-confirm"
        >
          <CheckCircle2 className="w-4 h-4 mr-1" />
          {saving ? 'Saving…' : `Yes, add these ${validCount} to my ${targetLabel}`}
        </Button>
      </div>
    </div>
  );
};

export default InstitutionImportPreview;
